import React, { useMemo } from "react";
import { StatusPill, EmptyState } from "../../../../components/ui/PageBits";
import { usePlatformStats, useShopStats, statusTone } from "./ownerBits";
import { getPlan, fmtLimit, excessShopTypes } from "../../../Booking/bookingPlans";

/**
 * Owner landing: platform totals, per-shop stats, shops needing attention.
 * No customer booking details here — those live under a specific shop.
 */
export default function OverviewTab({ plans, shops, resourceTypes, resources, bookings, onAddShop, onConfigure }) {
    const stats = usePlatformStats(shops, resources, bookings);
    const perShop = useShopStats(shops, resources, bookings, plans);

    const tiles = [
        { label: "Shops", value: stats.shops, sub: `${stats.activeShops} active`, icon: "🏪" },
        { label: "Active bookings", value: stats.active, sub: `${stats.pending} pending`, icon: "📅" },
        { label: "Resources", value: stats.resources, sub: "tables · doctors · rooms", icon: "🪑" },
        { label: "Pro shops", value: stats.proShops, sub: `${stats.shops - stats.proShops} on free`, icon: "⚡" },
    ];

    const sorted = [...shops].sort((a, b) =>
        (perShop[b.id]?.active || 0) - (perShop[a.id]?.active || 0)
    );

    const attention = useMemo(() => {
        const items = [];
        for (const s of shops) {
            const st = perShop[s.id];
            if (!st) continue;
            const reasons = [];
            if (s.status === "suspended" && st.active > 0) {
                reasons.push(`suspended with ${st.active} active booking${st.active === 1 ? "" : "s"}`);
            }
            if (st.locked > 0) reasons.push(`${st.locked} resource${st.locked === 1 ? "" : "s"} frozen by limits`);
            const overTypes = excessShopTypes(s, resourceTypes, plans).length;
            if (overTypes > 0) reasons.push(`${overTypes} calendar${overTypes === 1 ? "" : "s"} over type limit`);
            if (st.pending >= 3) reasons.push(`${st.pending} bookings waiting on confirmation`);
            if (reasons.length) items.push({ shop: s, reasons });
        }
        return items.sort((a, b) => b.reasons.length - a.reasons.length);
    }, [shops, perShop, resourceTypes, plans]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {tiles.map((t) => (
                    <div key={t.label} className="hk-card flex justify-between items-center">
                        <div>
                            <div className="hk-card-title" style={{ marginBottom: 4 }}>{t.label}</div>
                            <div className="text-3xl font-black hk-mono">{t.value}</div>
                            <div className="text-xs text-[#64748b] mt-1">{t.sub}</div>
                        </div>
                        <div className="w-11 h-11 rounded-2xl bg-[#f1f5f9] border border-[#cbd5e1] flex items-center justify-center text-xl shrink-0">
                            {t.icon}
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8">
                    <div className="hk-table-wrap">
                        <div className="px-6 pt-5 pb-3 flex flex-wrap justify-between items-center gap-2">
                            <h5 className="hk-card-title" style={{ margin: 0 }}>Shops at a glance</h5>
                            <button className="hk-btn hk-btn-dark hk-btn-sm" onClick={onAddShop}>
                                + Add shop
                            </button>
                        </div>
                        {sorted.length === 0 ? (
                            <EmptyState icon="🏪" title="No shops yet" hint="Onboard your first shop to start taking bookings." />
                        ) : (
                            <table className="hk-table">
                                <thead>
                                    <tr>
                                        <th>Shop</th>
                                        <th>Plan</th>
                                        <th>Resources</th>
                                        <th>Bookings</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((s) => {
                                        const st = perShop[s.id] || {};
                                        const plan = Object.values(plans ?? []).find(p => p.id === s.plan)
                                        return (
                                            <tr key={s.id}>
                                                <td>
                                                    <div className="font-bold">{s.business}</div>
                                                    <div className="text-xs text-[#64748b]">{s.owner}</div>
                                                </td>
                                                <td>
                                                    <StatusPill tone={plan?.label?.toLowerCase() === "pro" ? "amber" : "zinc"}>
                                                        {plan?.label}
                                                    </StatusPill>
                                                </td>
                                                <td className="hk-mono text-xs whitespace-nowrap">
                                                    {st.resources}/{fmtLimit(st.limit)}
                                                    {st.locked > 0 && <span className="text-[#b45309]"> · {st.locked} locked</span>}
                                                </td>
                                                <td className="hk-mono text-xs whitespace-nowrap">
                                                    {st.active} active
                                                    {st.pending > 0 && <span className="text-[#b45309]"> · {st.pending} pend</span>}
                                                </td>
                                                <td><StatusPill tone={statusTone(s.status)}>{s.status}</StatusPill></td>
                                                <td className="text-right">
                                                    <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => onConfigure(s.id)}>
                                                        Manage
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
                <div className="lg:col-span-4">
                    <div className="hk-card">
                        <h5 className="hk-card-title">Needs attention · {attention.length}</h5>
                        {attention.length === 0 ? (
                            <EmptyState icon="✅" title="All clear" hint="No suspended shops, frozen resources, or confirmation backlogs." />
                        ) : (
                            <div className="space-y-2">
                                {attention.map(({ shop, reasons }) => (
                                    <div key={shop.id} className="px-4 py-3 rounded-xl border border-[#fde68a] bg-[#fffbeb]">
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="font-bold text-sm truncate">{shop.business}</span>
                                            <button
                                                className="hk-btn hk-btn-outline hk-btn-sm shrink-0"
                                                onClick={() => onConfigure(shop.id)}
                                            >
                                                Manage
                                            </button>
                                        </div>
                                        <ul className="text-xs text-[#92400e] mt-1 space-y-0.5">
                                            {reasons.map((r, i) => (
                                                <li key={i}>• {r}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
